export const LimitType = {
  TRANSACTION: 'TRANSACTION',
  DAILY: 'DAILY',
} as const

export type LimitType = (typeof LimitType)[keyof typeof LimitType]

export const LimitLevel = {
  ACCOUNT: 'ACCOUNT',
  ALL_CUSTOMERS: 'ALL_CUSTOMERS',
  CUSTOMER: 'CUSTOMER',
} as const

export type LimitLevel = (typeof LimitLevel)[keyof typeof LimitLevel]

export const LimitOwner = {
  INTERNAL_USER: 'INTERNAL_USER',
  ACCOUNT_USER: 'ACCOUNT_USER',
  CUSTOMER_USER: 'CUSTOMER_USER',
} as const

export type LimitOwner = (typeof LimitOwner)[keyof typeof LimitOwner]

export interface LimitQualifiers {
  accountId: string | null
  customerId: string | null
  walletId: string | null
  bankingPartner: string | null
  method: string
}

export interface Limit {
  id: string
  level: LimitLevel
  limitType: LimitType
  amount: number
  currency: string
  method?: string
  qualifiers?: LimitQualifiers
  owner?: string
  zoneOffset?: string
  customerId?: string
  createdAt?: string
  updatedAt?: string
}

export interface CurrencyLimitsResponse {
  limits: Record<string, number>
}

export interface LimitsPageable {
  pageNumber: number
  pageSize: number
}

export interface LimitsResponse {
  content: Limit[]
  totalElements: number
  totalPages: number
  pageable: LimitsPageable
  hasNext: boolean
}

export type UpdateLimitPayload = Omit<Limit, 'owner' | 'qualifiers' | 'currency'> & {
  method: string
}

export interface LimitsQueryParams {
  pageNumber?: number
  pageSize?: number
  accountId?: string
  customerId?: string
  walletId?: string
  paymentMethod?: string
  limitType?: LimitType
  currency?: string
  level?: LimitLevel
}

export interface CreateLimitPayload {
  level: LimitLevel
  accountId: string
  customerId?: string
  method: string
  limitType: LimitType
  amount: number
  currency: string
  zoneOffset?: string | null
}

export interface AddLimitFormFields {
  currency: string
  method: string
  limitType: string
  amount: string
  zoneOffset?: string
}

export interface EditLimitFormFields {
  limitType: string
  amount: string
}

export const PaymentLimitSheetType = {
  ADD: 'add',
  EDIT: 'edit',
} as const

export type PaymentLimitSheetType =
  (typeof PaymentLimitSheetType)[keyof typeof PaymentLimitSheetType]

export interface PaymentLimitSheetProps {
  isOpen: boolean
  type: PaymentLimitSheetType
  limit?: Limit | null
  customerId?: string
  onClose: () => void
}

export interface GroupedLimit {
  method: string
  currency: string
  transactionLimit?: Limit
  dailyLimit?: Limit
}

export const PaymentLimitAction = {
  VIEW: 'view',
  EDIT: 'edit',
  DELETE: 'delete',
} as const

export type PaymentLimitActionType = (typeof PaymentLimitAction)[keyof typeof PaymentLimitAction]

export interface LimitHistoryUpdatedBy {
  userId: number
  email: string
  fullName: string
  tenantId: string
  isInternal: boolean
}

export interface LimitHistoryEntry {
  id: string
  level: LimitLevel
  limitType: LimitType
  amount: number
  currency: string
  qualifiers?: LimitQualifiers & { transferType?: string }
  owner?: string
  status: string
  zoneOffset?: string
  createdAt: string
  updatedAt: string
  updatedBy?: LimitHistoryUpdatedBy
}

export type LimitHistoryResponse = LimitHistoryEntry[]
